import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { tasksApi, type BulkResult } from "@/lib/tasks";
import { EmployeePicker } from "@/components/employee-picker";
import { usePermissions } from "@/hooks/use-permissions";
import { toast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export interface BulkTaskActionBarProps {
  selectedIds: number[];
  onDone: () => void;
}

function summarize(verb: string, result: BulkResult): string {
  const ok = result.results.filter((r) => r.success).length;
  const skipped = result.results.length - ok;
  return `${ok} ${verb}${skipped ? `, ${skipped} skipped` : ""}`;
}

export function BulkTaskActionBar({ selectedIds, onDone }: BulkTaskActionBarProps) {
  const { has } = usePermissions();
  const queryClient = useQueryClient();
  const canWrite = has("tasks:write");

  const [reassignOpen, setReassignOpen] = useState(false);
  const [assigneeId, setAssigneeId] = useState<number | null>(null);
  const [assigneeLabel, setAssigneeLabel] = useState<string | null>(null);
  const [reason, setReason] = useState("");

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ["tasks"] });

  const completeMutation = useMutation({
    mutationFn: () => tasksApi.bulkComplete(selectedIds),
    onSuccess: (result) => {
      toast.success(summarize("completed", result));
      invalidate();
      onDone();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const reassignMutation = useMutation({
    mutationFn: () => tasksApi.bulkReassign(selectedIds, assigneeId as number, reason || undefined),
    onSuccess: (result) => {
      toast.success(summarize("reassigned", result));
      invalidate();
      onDone();
      setReassignOpen(false);
      setAssigneeId(null);
      setAssigneeLabel(null);
      setReason("");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  if (selectedIds.length === 0) return null;

  return (
    <div className="flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2">
      <span className="text-sm font-medium">{selectedIds.length} selected</span>
      {canWrite && (
        <>
          <Button
            size="sm"
            onClick={() => completeMutation.mutate()}
            disabled={completeMutation.isPending}
          >
            Mark complete
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setReassignOpen(true)}
          >
            Reassign
          </Button>
        </>
      )}

      <Dialog open={reassignOpen} onOpenChange={setReassignOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reassign {selectedIds.length} task(s)</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="mb-1 block text-sm">Assignee</Label>
              <EmployeePicker
                value={assigneeId}
                valueLabel={assigneeLabel}
                onChange={(id, emp) => {
                  setAssigneeId(id);
                  setAssigneeLabel(emp?.fullName ?? null);
                }}
              />
            </div>
            <div>
              <Label htmlFor="bulk-reassign-reason" className="mb-1 block text-sm">
                Reason (optional)
              </Label>
              <Textarea
                id="bulk-reassign-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReassignOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => reassignMutation.mutate()}
              disabled={assigneeId == null || reassignMutation.isPending}
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
